import { DiffuseHalfLambertAoShader } from "./DiffuseHalfLambertAoShader";

/**
 * [DiffuseHalfLambertAoShader] with a baked lightmap multiplied into the result.
 *
 * The lightmap is projected orthographically onto the world space XY plane: its texture
 * coordinates are `worldPosition.xy * lmScale + 0.5`, i.e. the map is centred on the world
 * origin and `lmScale` is how many UV units one world unit covers (1 / scene size puts the
 * whole scene into the texture). It is multiplied into the lit color, so it darkens or tints
 * the baked lighting of the scene, and the fog is still applied over the result.
 *
 * The red channel of the lightmap drives a glow: it tints the surface from the ambient color
 * towards lmColor, and the glow fades out with height, from lmHeight.y upwards and gone at
 * lmHeight.x, so the baked light stays near the ground.
 *
 * Uniforms are the ones of the base shader plus the ones at the end of the list below:
 * `aoTexture` (bound by the renderer to the SSAO texture, as for the base shader) and the
 * lightmap sampler, scale, color, height and brightness, all taken from the draw commands.
 */
export class DiffuseHalfLambertAoLmShader extends DiffuseHalfLambertAoShader {
    lmTexture: WebGLUniformLocation | undefined;
    lmScale: WebGLUniformLocation | undefined;
    lmColor: WebGLUniformLocation | undefined;
    lmHeight: WebGLUniformLocation | undefined;
    lmBrightness: WebGLUniformLocation | undefined;

    /** @inheritdoc */
    fillCode() {
        this.vertexShaderCode = `precision highp float;
            uniform mat4 view_proj_matrix;
            uniform mat4 model_matrix;
            uniform float fogStartDistance;
            uniform float fogDistance;
            attribute vec4 rm_Vertex;
            attribute vec2 rm_TexCoord0;
            attribute vec3 rm_Normal;
            varying vec2 vTextureCoord;
            varying vec3 vWorldNormal;
            varying vec3 vWorldPosition;
            varying float vFogAmount;

            void main() {
              gl_Position = view_proj_matrix * rm_Vertex;
              vTextureCoord = rm_TexCoord0;
              vWorldNormal = (model_matrix * vec4(rm_Normal, 0.0)).xyz;
              vWorldPosition = (model_matrix * rm_Vertex).xyz;
              vFogAmount = clamp((length(gl_Position) - fogStartDistance) / fogDistance, 0.0, 1.0);
            }`;

        this.fragmentShaderCode = `precision mediump float;
            varying vec2 vTextureCoord;
            varying vec3 vWorldNormal;
            varying vec3 vWorldPosition;
            varying float vFogAmount;

            uniform sampler2D sTexture;
            // x, y, z = direction towards the light, w = half-Lambert exponent.
            uniform vec4 lightParams;
            uniform vec3 ambientColor;
            uniform vec4 fogColor;

            uniform sampler2D aoTexture;
            uniform vec2 inverseScreenSize;

            uniform sampler2D lmTexture;
            uniform vec2 lmScale;
            /** Glow color the lightmap fades the surface towards. */
            uniform vec3 lmColor;
            /** x = height at which the glow is gone, y = height at which the fade starts. */
            uniform vec2 lmHeight;
            /** How much the lightmap glow is scaled by before it is mixed in. */
            uniform float lmBrightness;

            void main() {
              vec4 color = texture2D(sTexture, vTextureCoord);

            //   color.rgb *= ambientColor;

              // Screen-space ambient occlusion, applied before the lighting.
              highp vec2 aoUV = gl_FragCoord.xy * inverseScreenSize;
              float ao = texture2D(aoTexture, aoUV).r;
              color.rgb *= ao;

              // Half-Lambert directional lighting plus the ambient light, which is what
              // keeps the surfaces facing away from the light from going black.
              vec3 lightDirection = normalize(lightParams.xyz);
              float nDotL = max(dot(normalize(vWorldNormal), lightDirection), 0.0);
              float halfLambert = pow(nDotL * 0.5 + 0.5, lightParams.w);
              color.rgb *= halfLambert;

              // Baked lightmap of the scene, sampled in the world space XY plane.
              vec2 pos = vWorldPosition.xy;
              vec2 lmUV = pos * lmScale + 0.5;
              float lmCoeff = texture2D(lmTexture, lmUV).r;
              lmCoeff *= lmBrightness;
              float d = vWorldPosition.z;
              float t = clamp((d - lmHeight.y) / (lmHeight.x - lmHeight.y), 0.0, 1.0);
              float fade = 1.0 - t * t;
              lmCoeff *= fade;
              vec3 glow = mix(ambientColor, lmColor, lmCoeff);

              color.rgb *= glow;

              // Linear fog, applied last.
              gl_FragColor = mix(color, fogColor, vFogAmount);
            }`;
    }

    /** @inheritdoc */
    fillUniformsAttributes() {
        super.fillUniformsAttributes();

        this.lmTexture = this.getUniform("lmTexture");
        this.lmScale = this.getUniform("lmScale");
        this.lmColor = this.getUniform("lmColor");
        this.lmHeight = this.getUniform("lmHeight");
        this.lmBrightness = this.getUniform("lmBrightness");

        // The base list plus the lightmap uniforms; the aoTexture entry is where the renderer
        // binds the SSAO texture, so a draw command has to carry a texture at that position.
        this.uniforms = this.uniforms!.concat([
            this.lmTexture,
            this.lmScale,
            this.lmColor,
            this.lmHeight,
            this.lmBrightness
        ]);
    }
}
